/**
 * Shared kernel, Service Location value object (H24). Where a Booking is
 * delivered: In-Clinic (at the Business) or At-Home (at the Consumer's address).
 * Used by Catalog (a Practitioner's offered locations, the Business's home-visit
 * surcharge) and Booking (a Booking's chosen location + effective price). A
 * platform-wide value, like Money, pure, no I/O, safe to import from client
 * components. Ubiquitous language: 01-discovery.md §1 "Service Location".
 */

export const SERVICE_LOCATIONS = ["in_clinic", "at_home"] as const;
export type ServiceLocation = (typeof SERVICE_LOCATIONS)[number];

export function isServiceLocation(value: unknown): value is ServiceLocation {
  return value === "in_clinic" || value === "at_home";
}

/**
 * Normalize an arbitrary array (a Postgres `text[]`, or repeated form fields)
 * into a valid, deduped, canonically-ordered set of Service Locations. Unknown
 * entries are dropped; order is fixed (in_clinic before at_home). Returns `[]`
 * when nothing valid remains, the caller decides the default (the domain
 * default is `["in_clinic"]`, enforced at the write edge and by the DB check).
 */
export function normalizeServiceLocations(raw: unknown): ServiceLocation[] {
  const list = Array.isArray(raw)
    ? raw.map(String)
    : typeof raw === "string"
      ? pgArrayLiteral(raw)
      : [];
  return SERVICE_LOCATIONS.filter((loc) => list.includes(loc));
}

/**
 * A Postgres array literal, `{in_clinic,at_home}`, as the driver hands it over
 * when it cannot parse the type itself.
 *
 * **This should never be reached, and it exists because it was.** Between 13
 * and 16 August 2026 the Workers database client ran with `fetch_types: false`
 * (`shared/db/client.ts`, which now explains itself at length), so every
 * `text[]` arrived as this string. The old `Array.isArray(raw) ? … : []` turned
 * that into an empty set, which meant **every Practitioner offered no Service
 * Location and every booking request was refused** with H24's honest-sounding
 * "In-Clinic visits aren't available for this time". The driver was fixed; this
 * is here so the same fault can never again be silent and wear a domain rule's
 * clothes.
 *
 * Deliberately **not** done for `categoryNames` in Discovery, which hit the
 * identical fault: that one throws `.map is not a function` and takes the
 * search page with it, which is loud, immediate and impossible to mistake for
 * a rule. Tolerate what fails silently; let what fails loudly keep failing
 * loudly.
 */
function pgArrayLiteral(raw: string): string[] {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return [];
  const inner = trimmed.slice(1, -1);
  if (inner === "") return [];
  return inner.split(",").map((part) => part.trim().replace(/^"|"$/g, ""));
}

/** Human label for UI copy (glossary language). */
export function serviceLocationLabel(location: ServiceLocation): string {
  return location === "at_home" ? "At-Home" : "In-Clinic";
}
